import React, { useEffect, useState } from "react";
import { List, Result, Button, Input, Alert, Empty, Card } from "antd";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@apollo/client";
import { GET_USER_EVENTS, GET_USER_ROLES_BY_EVENT, GET_PLACES_BY_EVENT } from "../graphql/queries";
import LocationAccuracyModal from "../components/LocationAccuracyModal";
import AnimatedLogo from "../components/AnimatedLogo";
import UrlQRScannerModal from "../components/UrlQRScannerModal";
import CreateTrackerModal from "../components/CreateTrackerModal";
import LinkTrackerModal from "../components/LinkTrackerModal";
import { Place, PlaceWithDistance, Role, Location, Event } from "../types";
import { useAppTheme } from "../theme/ThemeProvider";

const { Search } = Input;
const SPOTLIGHT_ADMIN_KEY = "spotlight-admin";
const ADMIN_SEARCH_PHRASE = "spotlight-tracker";
const FIXED_BUTTON_HEIGHT = 48;
const FIXED_BUTTON_GAP = 8;
const FIXED_BUTTON_BOTTOM = 16;

interface HomePageProps {
  userRolesdetail: Role[] | undefined;
  setUserRolesDetail: (roles: Role[]) => void;
  setPlaceDetail: (place: Place | null) => void;
  setPlacesForMap: (places: PlaceWithDistance[]) => void;
  location: Location | null;
  setLocation: (location: Location | null) => void;
}

export default function HomePage({
  userRolesdetail,
  setUserRolesDetail,
  setPlaceDetail,
  setPlacesForMap,
  location,
  setLocation,
}: HomePageProps) {
  const { themeName } = useAppTheme();
  const [places, setPlaces] = useState<PlaceWithDistance[] | undefined>(undefined);
  const [filtered, setFiltered] = useState<PlaceWithDistance[] | undefined>(undefined);
  const [searchTerm, setSearchTerm] = useState("");
  const [geoDenied] = useState(false);
  const [error] = useState<string | null>(null);
  const [showAccuracyModal, setShowAccuracyModal] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const [showEventSelection, setShowEventSelection] = useState(false);
  const [showUrlScanner, setShowUrlScanner] = useState(false);
  const [showCreateTracker, setShowCreateTracker] = useState(false);
  const [showLinkTracker, setShowLinkTracker] = useState(false);
  const [isAdmin, setIsAdmin] = useState(
    () => localStorage.getItem(SPOTLIGHT_ADMIN_KEY) !== null
  );

  const navigate = useNavigate();
  const userId = localStorage.getItem("userId") || "";

  // Получаем события пользователя
  const { data: eventsData, loading: loadingEvents } = useQuery<{ getUserEvents: { events: Event[]; success: boolean } }>(
    GET_USER_EVENTS,
    {
      variables: { input: { userId } },
      skip: !userId,
      onCompleted: (data) => {
        const events = data.getUserEvents.events || [];
        const savedEventId = localStorage.getItem("eventId");

        // Проверяем, есть ли сохраненное событие в списке
        if (savedEventId && events.some((e) => e.id === savedEventId)) {
          const savedEvent = events.find((e) => e.id === savedEventId);
          if (savedEvent) {
            setSelectedEvent(savedEvent);
            setShowEventSelection(false);
            return;
          }
        }

        // Если событие одно - выбираем его автоматически
        if (events.length === 1) {
          setSelectedEvent(events[0]);
          localStorage.setItem("eventId", events[0].id);
          setShowEventSelection(false);
        } else if (events.length > 1) {
          // Если событий несколько - показываем выбор
          setShowEventSelection(true);
        } else {
          // Если событий нет - показываем Empty
          setShowEventSelection(false);
        }
      },
    }
  );

  const events = eventsData?.getUserEvents?.events || [];
  const eventId = selectedEvent?.id || localStorage.getItem("eventId") || "";

  // Получаем роли пользователя для события
  const { data: rolesData, loading: loadingRoles } = useQuery<{ getUserRolesByEvent: { roles: Role[]; success: boolean } }>(
    GET_USER_ROLES_BY_EVENT,
    {
      variables: { input: { userId, eventId } },
      skip: !userId || !eventId || !location || showEventSelection,
    }
  );

  // Получаем места для события
  const placesQueryEnabled = Boolean(
    userId && eventId && location && !showEventSelection
  );
  const { data: placesData, loading: loadingPlaces } = useQuery<{ getPlacesByEvent: { places: PlaceWithDistance[]; success: boolean } }>(
    GET_PLACES_BY_EVENT,
    {
      variables: {
        input: {
          userId,
          eventId,
          latitude: location?.latitude || 0,
          longitude: location?.longitude || 0,
        },
      },
      skip: !placesQueryEnabled,
      fetchPolicy: "cache-and-network",
      nextFetchPolicy: "cache-first",
      notifyOnNetworkStatusChange: true,
    }
  );

  const handleEventSelect = (event: Event) => {
    setSelectedEvent(event);
    localStorage.setItem("eventId", event.id);
    setShowEventSelection(false);
  };

  useEffect(() => {
    if (rolesData?.getUserRolesByEvent?.roles) {
      const roleIds = rolesData.getUserRolesByEvent.roles.map((role) => role.id);
      localStorage.setItem("userRoles", JSON.stringify(roleIds));
      setUserRolesDetail(rolesData.getUserRolesByEvent.roles);
    }
  }, [rolesData, setUserRolesDetail]);

  useEffect(() => {
    if (placesData?.getPlacesByEvent?.places) {
      const sorted = [...placesData.getPlacesByEvent.places].sort((a, b) => {
        const aHasGeo = a.geo_location !== null && typeof a.distance === "number";
        const bHasGeo = b.geo_location !== null && typeof b.distance === "number";

        if (aHasGeo && bHasGeo) {
          return (a.distance || 0) - (b.distance || 0);
        } else if (aHasGeo) {
          return -1;
        } else if (bHasGeo) {
          return 1;
        } else {
          return 0;
        }
      });
      setPlaces(sorted);
      setFiltered(sorted);
      setPlacesForMap(placesData.getPlacesByEvent.places);
    }
  }, [placesData, setPlacesForMap]);


  useEffect(() => {
    const term = searchTerm.toLowerCase();
    if (places) {
      setFiltered(places.filter((p) => p.name.toLowerCase().includes(term)));
    }
  }, [searchTerm, places]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        setLocation(null);
        setShowAccuracyModal(true);
        setPlaces(undefined);
        setFiltered([]);
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [setLocation]);

  const handleAcceptLocation = (coords: GeolocationCoordinates) => {
    setLocation({ latitude: coords.latitude, longitude: coords.longitude });
    setShowAccuracyModal(false);
  };

  const handleAutoAccept = (coords: GeolocationCoordinates) => {
    setLocation({ latitude: coords.latitude, longitude: coords.longitude });
    setShowAccuracyModal(false);
  };

  const formatDistance = (meters?: number) => {
    if (!meters) return "";
    if (meters >= 1000) {
      return `${(meters / 1000).toFixed(2)} km`;
    } else {
      return `${Math.round(meters)} m`;
    }
  };

  if (geoDenied) {
    return (
      <div style={{ padding: 16 }}>
        <Result
          status="500"
          title="Přístup k poloze byl zablokován"
          subTitle="Abychom mohli zobrazit místa poblíž vás, musíte povolit přístup k poloze v nastavení prohlížeče."
          extra={
            <Button type="primary" onClick={() => window.location.reload()}>
              Zkusit znovu
            </Button>
          }
        />
      </div>
    );
  }

  // Показываем загрузку пока получаем события
  if (loadingEvents) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "60vh",
        }}
      >
        <AnimatedLogo loading="spinner" withBackground={false} />
      </div>
    );
  }

  // Если событий нет - показываем Empty
  if (!loadingEvents && events.length === 0) {
    return (
      <div style={{ padding: 16 }}>
        <Empty
          description="Žádné události"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      </div>
    );
  }

  // Если нужно показать выбор события
  if (showEventSelection && events.length > 1) {
    return (
      <div style={{ padding: 16 }}>
        <h2 style={{ marginBottom: 24 }}>Vyberte událost</h2>
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {events.map((event) => (
            <Card
              key={event.id}
              hoverable
              onClick={() => handleEventSelect(event)}
              style={{ cursor: "pointer" }}
            >
              <div>
                <h3 style={{ margin: 0, marginBottom: 8 }}>{event.name}</h3>
                {event.description && (
                  <p style={{ margin: 0, color: "#666" }}>{event.description}</p>
                )}
                {event.start_date && (
                  <p style={{ margin: "8px 0 0 0", fontSize: 12, color: "#999" }}>
                    Začátek: {new Date(event.start_date).toLocaleDateString()}
                  </p>
                )}
              </div>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  const loading = loadingRoles || loadingPlaces;

  const handleSearch = (value: string) => {
    const term = value.trim();
    if (term.toLowerCase() === ADMIN_SEARCH_PHRASE.toLowerCase()) {
      localStorage.setItem(SPOTLIGHT_ADMIN_KEY, "true");
      setIsAdmin(true);
      setSearchTerm("");
    }
  };

  const fixedButtonStep = FIXED_BUTTON_HEIGHT + FIXED_BUTTON_GAP;
  const scanButtonBottom = FIXED_BUTTON_BOTTOM;
  const linkButtonBottom = scanButtonBottom + fixedButtonStep;
  const createButtonBottom = linkButtonBottom + fixedButtonStep;
  const listBottomPadding = isAdmin
    ? createButtonBottom + FIXED_BUTTON_HEIGHT + FIXED_BUTTON_GAP
    : scanButtonBottom + FIXED_BUTTON_HEIGHT + FIXED_BUTTON_GAP;

  const fixedButtonStyle = (bottom: number): React.CSSProperties => ({
    position: "fixed",
    left: 16,
    right: 16,
    bottom,
    height: FIXED_BUTTON_HEIGHT,
    fontSize: 16,
    zIndex: 900,
  });

  return (
    <div style={{ padding: 16 }}>
      {!location && (
        <LocationAccuracyModal
          visible={showAccuracyModal}
          onAccept={handleAcceptLocation}
          onAutoAccept={handleAutoAccept}
        />
      )}

      {error && (
        <Alert type="error" message={error} style={{ marginBottom: 16 }} />
      )}
      {!loading && (
        <div
          style={{
            position: "sticky",
            top: 0,
            zIndex: 100,
            paddingBottom: 16,
            paddingTop: 16,
          }}
        >
          <Search
            placeholder="Hledat místo"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onSearch={handleSearch}
            allowClear
            style={{ marginBottom: 16 }}
          />
        </div>
      )}
      {loading || !filtered ? (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "60vh",
          }}
        >
          <AnimatedLogo loading="spinner" withBackground={false} />
        </div>
      ) : filtered && filtered?.length === 0 ? (
        <Result
          status="info"
          title="Žádná místa nenalezena"
          subTitle="Zkuste změnit hledaný výraz nebo povolit polohu."
        />
      ) : (
        <div
          style={{
            maxHeight: "calc(100dvh - 220px)",
            overflowY: "auto",
            paddingBottom: listBottomPadding,
          }}
        >
          <List
            bordered={false}
            dataSource={filtered}
            renderItem={(item) => (
              <List.Item
                key={item.id}
                onClick={() => {
                  setPlaceDetail({
                  id: item.id,
                  name: item.name,
                  description: item.description,
                  geo_location: item.geo_location,
                  loc_id: item.loc_id,
                  loc_name: item.loc_name,
                });
                  navigate(`/place/${item.id}`);
                }}
                className={`places-list-item ${themeName === "dark" ? "places-list-item--dark" : "places-list-item--light"}`}
              >
                <List.Item.Meta title={item.name} description={item.loc_name} />
                <div>{formatDistance(item.distance)}</div>
              </List.Item>
            )}
          />
        </div>
      )}
      {isAdmin && (
        <>
          <Button
            style={fixedButtonStyle(createButtonBottom)}
            onClick={() => setShowCreateTracker(true)}
          >
            Vytvořit tracker
          </Button>
          <Button
            style={fixedButtonStyle(linkButtonBottom)}
            onClick={() => setShowLinkTracker(true)}
          >
            Propojit tracker
          </Button>
        </>
      )}
      <Button
        type="primary"
        style={fixedButtonStyle(scanButtonBottom)}
        onClick={() => setShowUrlScanner(true)}
      >
        Skenovat QR
      </Button>
      <CreateTrackerModal
        open={showCreateTracker}
        onClose={() => setShowCreateTracker(false)}
      />
      <LinkTrackerModal
        open={showLinkTracker}
        onClose={() => setShowLinkTracker(false)}
        places={places || []}
      />
      <UrlQRScannerModal
        visible={showUrlScanner}
        onClose={() => setShowUrlScanner(false)}
        onUrlScan={(url) => {
          setShowUrlScanner(false);
          window.location.href = url;
        }}
      />
    </div>
  );
}
